import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  CircularProgress,
  Alert,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useGetMeQuery, useChangePasswordMutation } from '../../../store/api/authApi';

const FieldRow = ({ icon: Icon, label, value }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 2,
      py: 1.5,
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-of-type': { borderBottom: 0 },
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 2,
        bgcolor: 'rgba(78, 205, 196, 0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 20, color: '#4ECDC4' }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500, color: '#2C3E50' }}>
        {value ?? '—'}
      </Typography>
    </Box>
  </Box>
);

function Profile() {
  const { data: profile, isLoading, error } = useGetMeQuery();
  const [changePassword, { isLoading: changingPassword, error: changeError, isSuccess: changeSuccess }] =
    useChangePasswordMutation();

  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const openChangePasswordDialog = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setChangePasswordDialogOpen(true);
  };

  const closeChangePasswordDialog = () => {
    setChangePasswordDialogOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) return;
    try {
      await changePassword({ currentPassword, newPassword }).unwrap();
      closeChangePasswordDialog();
    } catch (_) {
      // Error shown via changeError
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#4ECDC4' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
          Profile
        </Typography>
        <Alert severity="error">{error?.data?.message || 'Failed to load profile.'}</Alert>
      </Box>
    );
  }

  const user = profile?.user ?? profile;
  const company = user?.company;
  const role = user?.role;

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ color: '#4ECDC4', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: 1 }}
      >
        Account
      </Typography>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 700, color: '#2C3E50' }}>
        Profile
      </Typography>

      <Grid container spacing={3}>
        {/* Profile info card */}
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.06)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                minHeight: 120,
                background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 3,
                px: 3,
                py: 3,
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'white',
                  color: '#4ECDC4',
                  fontSize: '2rem',
                  fontWeight: 700,
                  border: '4px solid white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                {user?.firstName?.[0]?.toUpperCase() || user?.username?.[0]?.toUpperCase() || 'U'}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    mb: 0.5,
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  }}
                >
                  {user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user?.username || 'User'}
                </Typography>
                {user?.email && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255,255,255,0.95)',
                      fontSize: '0.9375rem',
                    }}
                  >
                    {user.email}
                  </Typography>
                )}
              </Box>
            </Box>
            <CardContent sx={{ pt: 3, px: 3, pb: 3 }}>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FieldRow icon={PersonIcon} label="First name" value={user?.firstName} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FieldRow icon={PersonIcon} label="Last name" value={user?.lastName} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FieldRow icon={EmailIcon} label="Email" value={user?.email} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FieldRow icon={BadgeIcon} label="Username" value={user?.username} />
                </Grid>
                {user?.id != null && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FieldRow icon={BadgeIcon} label="User ID" value={String(user.id)} />
                  </Grid>
                )}
                {company && (
                  <>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FieldRow icon={BusinessIcon} label="Company" value={company?.name} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FieldRow icon={BusinessIcon} label="Company code" value={company?.code} />
                    </Grid>
                  </>
                )}
                {role && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FieldRow icon={BadgeIcon} label="Role" value={role?.name} />
                  </Grid>
                )}
              </Grid>

              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={openChangePasswordDialog}
                sx={{
                  mt: 3,
                  borderColor: '#4ECDC4',
                  color: '#4ECDC4',
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#3BA89F',
                    bgcolor: 'rgba(78, 205, 196, 0.08)',
                  },
                }}
              >
                Change password
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Change password dialog */}
      <Dialog
        open={changePasswordDialogOpen}
        onClose={closeChangePasswordDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Change password</DialogTitle>
        <form onSubmit={handleChangePassword}>
          <DialogContent>
            {changeError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {changeError?.data?.message || 'Failed to change password.'}
              </Alert>
            )}
            <TextField
              fullWidth
              type={showCurrent ? 'text' : 'password'}
              label="Current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              size="small"
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowCurrent((s) => !s)}
                      aria-label="toggle current password visibility"
                    >
                      {showCurrent ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              type={showNew ? 'text' : 'password'}
              label="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              size="small"
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowNew((s) => !s)}
                      aria-label="toggle new password visibility"
                    >
                      {showNew ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              type={showConfirm ? 'text' : 'password'}
              label="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              size="small"
              error={confirmPassword.length > 0 && newPassword !== confirmPassword}
              helperText={
                confirmPassword.length > 0 && newPassword !== confirmPassword
                  ? 'Passwords do not match'
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowConfirm((s) => !s)}
                      aria-label="toggle confirm password visibility"
                    >
                      {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeChangePasswordDialog} color="inherit">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                changingPassword ||
                !currentPassword ||
                !newPassword ||
                !confirmPassword ||
                newPassword !== confirmPassword
              }
              sx={{
                bgcolor: '#4ECDC4',
                '&:hover': { bgcolor: '#3BA89F' },
              }}
            >
              {changingPassword ? 'Updating…' : 'Update password'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Profile;
